import { acceptHMRUpdate, defineStore } from 'pinia';
import type { KeyboardLayout } from '@/graphql/graphql';
import {
  DEFAULT_KEYBOARD_LAYOUT,
  DEFAULT_LOCALE,
  IS_PRODUCTION,
} from '@/lib/constants';
import { convertKeysFromQwerty, getKey } from '@/lib/keyboard-layout';
import { lookupLocaleKeyboardLayout } from '@/lib/locale';

type State = {
  isProduction: boolean;
  keyboardLayout: KeyboardLayout;
  locale: Locale;
};

export const useMainStore = defineStore('main', {
  state: (): State => ({
    isProduction: IS_PRODUCTION,
    keyboardLayout: DEFAULT_KEYBOARD_LAYOUT,
    locale: DEFAULT_LOCALE,
  }),
  actions: {
    reset(): void {
      this.$reset();
    },
    getFullKey(key: string): Key {
      return getKey(this.keyboardLayout, key);
    },
    convertKeys<T extends string | string[]>(keys: T): T {
      const separator = '';

      const isArray = Array.isArray(keys);

      const allKeys = isArray ? keys : keys.split(separator);

      const convertedKeys = convertKeysFromQwerty(this.keyboardLayout, allKeys);

      return (isArray ? convertedKeys : convertedKeys.join(separator)) as T;
    },
    lookupContent<T>(
      lookup: Record<Locale, Record<KeyboardLayout, T | undefined>>,
    ): T {
      return lookupLocaleKeyboardLayout<T>(
        this.locale,
        this.keyboardLayout,
        lookup,
      );
    },
  },
  persist: true,
});

if (module.hot) {
  module.hot.accept(acceptHMRUpdate(useMainStore, module.hot));
}
